import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../containers/UseAuth';
import { newCampaignActions, notificationsActions } from '../../../store';
import Location from './Location/Location';
import NearPlace from './NearPlace/NearPlace';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';
import 'rc-slider/assets/index.css';
import Hint from '../../Tools/Hint';
import { arrayToCsv } from '../../../utils/functions';
import { RequiredTag } from '../../Input/RequiredTag';
import { RequiredFields } from '../../Input/RequiredFields';

/**
 * A saved campaign restores its geo selections as plain ids (names on legacy
 * campaigns), so every level has to be re-matched against its options once they
 * finish loading. Values with no matching option are dropped, which is also what
 * keeps the children consistent when the parent selection changes.
 */
const resolveGeoSelection = (selected, options, getOptionId) => {
    if (!Array.isArray(selected) || selected.length === 0) return [];

    if (!Array.isArray(options) || options.length === 0) {
        // Options are still loading (the first pass runs before the parent level
        // has been resolved), so keep the pending ids around for the next pass.
        // Already resolved options are dropped: an empty option list means they no
        // longer belong to the current parent selection.
        const isResolved = (item) => item !== null && typeof item === 'object';
        return selected.every(isResolved) ? [] : selected;
    }

    const key = (value) =>
        String(value ?? '')
            .trim()
            .toLowerCase();
    const byId = new Map();
    const byName = new Map();

    options.forEach((option) => {
        const id = key(getOptionId(option));
        if (id && !byId.has(id)) byId.set(id, option);

        const name = key(option?.name);
        if (name && !byName.has(name)) byName.set(name, option);
    });

    const resolved = [];
    const seen = new Set();

    selected.forEach((item) => {
        const isObject = item !== null && typeof item === 'object';
        const id = key(isObject ? getOptionId(item) : item);
        const name = key(isObject ? item?.name : item);
        const match = byId.get(id) || byName.get(name);

        if (!match) return;

        const matchId = key(getOptionId(match));
        if (seen.has(matchId)) return;

        seen.add(matchId);
        resolved.push(match);
    });

    return resolved;
};

const byGeoId = (option) => option?.id;
const byCountryCode = (option) => option?.iso2 ?? option?.country_id;

const WizardLocation = forwardRef(function WizardLocation(
    {
        isActive,
        zones,
        isResizeMapOn,
        location,
        selectedCurrency,
        dispatch,
        service,
        filteredScreensLocal,
        newCampaign,
        setFilterScreenByPoi,
        filterScreenByPoi,
        setLocation,
    },
    ref
) {
    const { t } = useTranslation();

    //? los errores viven dentro del componente, no hacen falta dentro de createCampaignPage
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({
        selectedCountries: location.selectedCountries.length || false,
    });

    function validateLocation(location) {
        const errors = {};

        if (
            !location.selectedCountries ||
            location.selectedCountries.length === 0
        ) {
            errors.selectedCountries = t(
                'sections.campaigns.new-campaign.wizard-basics.location.validations.countries'
            );
        }

        const isValid = Object.keys(errors).length === 0;
        return { isValid, errors };
    }

    //? Exponer método validate() al padre (createCampaignPage)
    useImperativeHandle(ref, () => ({
        validate() {
            const { isValid, errors: nextErrors } = validateLocation(location);
            setErrors(nextErrors);

            setTouched({
                selectedCountries: true,
            });

            return isValid;
        },
    }));

    useEffect(() => {
        async function fetchCountries() {
            if (!selectedCurrency) return;
            try {
                const response = await service.getCountriesByCurrencyId(
                    selectedCurrency.short_name
                );

                if (!response?.countries?.length) {
                    dispatch(
                        notificationsActions.setNotification({
                            type: 'warning',
                            title: t(
                                'sections.management.section.billing.budget.tab'
                            ),
                            message: t(
                                'sections.management.section.billing.budget.notifications.currencyWihoutScreen'
                            ),
                        })
                    );
                }

                setLocation((prev) => ({
                    ...prev,
                    countries: response?.countries || [],
                    selectedCountries: resolveGeoSelection(
                        prev.selectedCountries,
                        response?.countries || [],
                        byCountryCode
                    ),
                }));
            } catch (err) {
                console.error('Error cargando países:', err);
            }
        }

        fetchCountries();
    }, [selectedCurrency]);

    useEffect(() => {
        let cancelled = false;

        async function fetchRegions() {
            const selected = location.selectedCountries || [];
            if (selected.length === 0) {
                setLocation((prev) => ({
                    ...prev,
                    regions: [],
                    selectedRegions: [],
                    cities: [],
                    selectedCities: [],
                    neighborhood: [],
                    selectedNeighborhoods: [],
                }));
                return;
            }

            try {
                const idsCsv = arrayToCsv(selected);
                if (idsCsv.length === 0) return;
                const rows = await service.getRegionsByCountryIds(idsCsv);
                if (cancelled) return;
                // A failed request must not wipe what is already selected.
                if (!Array.isArray(rows)) return;

                setLocation((prev) => ({
                    ...prev,
                    regions: rows,
                    selectedRegions: resolveGeoSelection(
                        prev.selectedRegions,
                        rows,
                        byGeoId
                    ),
                }));
            } catch (err) {
                console.error('Error cargando regiones:', err);
            }
        }

        fetchRegions();
        return () => {
            cancelled = true;
        };
    }, [location.selectedCountries]);

    useEffect(() => {
        let cancelled = false;

        async function fetchCities() {
            const selected = location.selectedRegions || [];
            if (selected.length === 0) {
                setLocation((prev) => ({
                    ...prev,
                    cities: [],
                    selectedCities: [],
                    neighborhood: [],
                    selectedNeighborhoods: [],
                }));
                return;
            }

            try {
                const idsCsv = arrayToCsv(selected);
                const rows = await service.getCitiesByRegionIds(idsCsv);

                if (cancelled) return;
                if (!Array.isArray(rows)) return;

                // rows = [{ id, name, region_id }]
                setLocation((prev) => ({
                    ...prev,
                    cities: rows,
                    selectedCities: resolveGeoSelection(
                        prev.selectedCities,
                        rows,
                        byGeoId
                    ),
                }));
            } catch (err) {
                console.error('Error cargando ciudades:', err);
            }
        }

        fetchCities();
        return () => {
            cancelled = true;
        };
    }, [location.selectedRegions]);

    useEffect(() => {
        let cancelled = false;

        async function fetchAreas() {
            const selected = location.selectedCities || [];
            if (selected.length === 0) {
                setLocation((prev) => ({
                    ...prev,
                    neighborhood: [],
                    selectedNeighborhoods: [],
                }));
                return;
            }

            try {
                const idsCsv = arrayToCsv(selected);
                const rows = await service.getAreasByCityIds(idsCsv);

                if (cancelled) return;
                if (!Array.isArray(rows)) return;

                // rows = [{ id, name, region_id }]
                setLocation((prev) => ({
                    ...prev,
                    neighborhood: rows,
                    selectedNeighborhoods: resolveGeoSelection(
                        prev.selectedNeighborhoods,
                        rows,
                        byGeoId
                    ),
                }));
            } catch (err) {
                console.error('Error cargando ciudades:', err);
            }
        }

        fetchAreas();
        return () => {
            cancelled = true;
        };
    }, [location.selectedCities]);

    return (
        <>
            {!isActive ? (
                ''
            ) : (
                <div
                    className={`basics mt-2 h-full overflow-visible overflow-y-auto text-sm transition-transform duration-299 ${
                        isResizeMapOn
                            ? 'transform scale-75 -translate-y-20'
                            : ''
                    }`}>
                    <div className="text-xs text-gray-500 ml-4 pt-4 -mb-1 font-semibold">
                        * {t('sections.campaigns.new-campaign.required-fields')}
                    </div>
                    <div className="module rounded m-1 mb-4">
                        <div className="header flex justify-between content-center">
                            <div className="title text-xl font-extralight">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-basics.location.title'
                                )}
                            </div>
                            <RequiredFields></RequiredFields>
                        </div>

                        <div className="body rounded-sm p-1 flex flex-col gap-5">
                            <label className="block md:w-3/6 -mb-2">
                                <span className="block text-sm font-medium text-gray-700">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-basics.location.countries-label'
                                    )}{' '}
                                    <RequiredTag
                                        alert={errors.selectedCountries}
                                    />
                                </span>
                                <Select
                                    inputId="countries-select"
                                    options={location.countries}
                                    getOptionLabel={(c) => c.name}
                                    getOptionValue={(c) => String(c?.iso2)}
                                    value={location.selectedCountries}
                                    onChange={(vals) => {
                                        setLocation({
                                            ...location,
                                            selectedCountries: vals,
                                        });

                                        setTouched((prev) => ({
                                            ...prev,
                                            selectedCountries: true,
                                        }));

                                        if (touched.selectedCountries) {
                                            const { errors: nextErrors } =
                                                validateLocation({
                                                    ...location,
                                                    selectedCountries: vals,
                                                });
                                            setErrors(nextErrors);
                                        }
                                    }}
                                    isMulti
                                    placeholder={t(
                                        'sections.campaigns.new-campaign.wizard-basics.location.countries-placeholder'
                                    )}
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            borderColor:
                                                errors.selectedCountries
                                                    ? '#ef4444'
                                                    : base.borderColor, // red-500
                                            boxShadow: errors.selectedCountries
                                                ? '0 0 0 1px #ef4444'
                                                : base.boxShadow,
                                            '&:hover': {
                                                borderColor:
                                                    errors.selectedCountries
                                                        ? '#ef4444'
                                                        : base.borderColor,
                                            },
                                        }),
                                    }}
                                />
                            </label>
                            {
                                <label className="block md:w-3/6 -mb-2">
                                    <span className="block text-sm font-medium text-gray-700">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-basics.location.regions-label'
                                        )}
                                    </span>
                                    <Select
                                        inputId="regions-select"
                                        options={location.regions}
                                        value={location.selectedRegions}
                                        getOptionLabel={(c) => c.name}
                                        getOptionValue={(c) => String(c?.id)}
                                        onChange={(vals) =>
                                            setLocation({
                                                ...location,
                                                selectedRegions: vals,
                                            })
                                        }
                                        isDisabled={
                                            location.selectedCountries.length ==
                                            0
                                        }
                                        isMulti={true}
                                        isSearchable={true}
                                        placeholder={t(
                                            'sections.campaigns.new-campaign.wizard-basics.location.regions-placeholder'
                                        )}
                                    />
                                </label>
                            }
                            {
                                <label className="block md:w-3/6 -mb-2">
                                    <span className="block text-sm font-medium text-gray-700">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-basics.location.cities-label'
                                        )}
                                    </span>
                                    <Select
                                        inputId="cities-selector"
                                        options={location.cities}
                                        value={location.selectedCities}
                                        getOptionLabel={(c) => c.name}
                                        getOptionValue={(c) => String(c?.id)} // ← identificador estable (string)
                                        onChange={(vals) => {
                                            setLocation({
                                                ...location,
                                                selectedCities: vals,
                                            });
                                        }}
                                        isMulti={true}
                                        isSearchable={true}
                                        placeholder={t(
                                            'sections.campaigns.new-campaign.wizard-basics.location.cities-placeholder'
                                        )}
                                        isDisabled={
                                            location.selectedRegions?.length ==
                                            0
                                        }
                                    />
                                </label>
                            }
                            {
                                <label className="block md:w-3/6 -mb-2">
                                    <span className="block text-sm font-medium text-gray-700">
                                        {t(
                                            'sections.campaigns.new-campaign.wizard-basics.location.neighbourhood-label'
                                        )}
                                    </span>
                                    <Select
                                        inputId="neighbourhood-select"
                                        options={location.neighborhood}
                                        value={location.selectedNeighborhoods}
                                        getOptionLabel={(c) => c.name}
                                        getOptionValue={(c) => String(c?.id)}
                                        onChange={(vals) => {
                                            setLocation({
                                                ...location,
                                                selectedNeighborhoods: vals,
                                            });
                                        }}
                                        isMulti
                                        isSearchable
                                        placeholder={t(
                                            'sections.campaigns.new-campaign.wizard-basics.location.neighbourhood-placeholder'
                                        )}
                                        isDisabled={
                                            location.selectedCities.length === 0
                                        }
                                    />
                                </label>
                            }
                        </div>
                    </div>

                    <NearPlace
                        zones={zones}
                        dispatch={dispatch}
                        newCampaign={newCampaign}
                        location={location}
                        filteredScreensLocal={filteredScreensLocal}
                        setFilterScreenByPoi={setFilterScreenByPoi}
                        filterScreenByPoi={filterScreenByPoi}
                    />
                </div>
            )}
        </>
    );
});

WizardLocation.proptyTypes = {
    isActive: PropTypes.bool.isRequired,
    zones: PropTypes.object,
    isResizeMapOn: PropTypes.bool,
    service: PropTypes.func,
    location: PropTypes.object,
    selectedCurrency: PropTypes.object,
    dispatch: PropTypes.func,
    filteredScreens: PropTypes.array,
    newCampaign: PropTypes.object,
    setFilterScreenByPoi: PropTypes.func,
    filterScreenByPoi: PropTypes.bool,
};

export default WizardLocation;
